import * as THREE from "three";
import { parseCategorizationCsv } from "./csv.js";
import { layoutWords } from "./layout.js";
import { createGalaxy } from "./galaxy.js";
import { createCameraRig } from "./camera.js";
import { pickNearest, collectNearbyLabels } from "./picking.js";
import { createLabelLayer, createClusterLabels } from "./labels.js";
import { createUI } from "./ui.js";
import { CATEGORIES, primaryCategory } from "./categories.js";
import {
  applyThemeToDocument,
  getTheme,
  loadThemeId,
  saveThemeId,
} from "./themes.js";

const canvas = document.querySelector("#galaxy");
const overlay = document.querySelector("#labels");
const loader = document.querySelector("#loader");
const loaderBar = document.querySelector("#loader-bar");
const loaderCopy = document.querySelector("#loader-copy");

const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  alpha: false,
  powerPreference: "high-performance",
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.NoToneMapping;
canvas.style.touchAction = "none";

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.15, 900);
const rig = createCameraRig(camera, canvas);
const ui = createUI();
const labels = createLabelLayer(overlay);
const clusterLabels = createClusterLabels(overlay);
const clock = new THREE.Clock();
const tmp = new THREE.Vector3();
const clusterTmp = new THREE.Vector3();

let rows = [];
let galaxy = null;
let selected = -1;
let hovered = -1;
let pointerNdc = { x: 0, y: 0 };
let pointerInside = false;
let downX = 0;
let downY = 0;
let fpsAccum = 0;
let fpsFrames = 0;
let labelTimer = 0;
let hoverTimer = 0;
const baseSizes = [];
let theme = getTheme(loadThemeId());
let activeCategories = new Set(CATEGORIES);

applyAppearance(theme);

function applyAppearance(next) {
  theme = next;
  applyThemeToDocument(next);
  renderer.setClearColor(next.bg, 1);
  scene.fog = new THREE.FogExp2(next.bg, next.fog);
}

function setLoader(progress, copy) {
  loaderBar.style.width = `${Math.round(progress * 100)}%`;
  loaderCopy.textContent = copy;
}

async function boot() {
  setLoader(0.12, "Loading categorization output");
  const response = await fetch(`${import.meta.env.BASE_URL}data/words.csv`, {
    cache: "no-store",
  });
  if (!response.ok) throw new Error(`Failed to load CSV (${response.status})`);
  const text = await response.text();
  setLoader(0.42, "Parsing 15,000 ML rows");
  rows = parseCategorizationCsv(text);
  setLoader(0.68, "Placing clusters from category weights");
  const layout = layoutWords(rows);
  galaxy = createGalaxy(rows, layout);
  galaxy.applyTheme(theme, rows);
  scene.add(galaxy.group);
  for (let i = 0; i < layout.sizes.length; i += 1) baseSizes[i] = layout.sizes[i];
  ui.setCounts(rows.length);
  ui.bindSearch(rows, selectWord);
  ui.bindLegend(rows, toggleCategory, showAllCategories);
  ui.setLegendState(activeCategories, theme.categoryColors);
  ui.bindThemes(theme.id, setTheme);
  ui.bindCamera(
    () => rig.dolly(0.72),
    () => {
      if (rig.isFollowing()) overview();
      else rig.dolly(1.38);
    },
    overview,
  );
  setLoader(1, "Entering the galaxy");
  loader.classList.add("is-gone");
  document.body.dataset.ready = "1";
  renderer.setAnimationLoop(frame);
}

function setTheme(id) {
  const next = getTheme(id);
  applyAppearance(next);
  saveThemeId(next.id);
  if (galaxy) galaxy.applyTheme(next, rows);
  ui.setActiveTheme(next.id);
  ui.setLegendState(activeCategories, next.categoryColors);
  if (selected >= 0) {
    const row = rows[selected];
    const primary = primaryCategory(row.scores);
    ui.openDetail(
      row,
      `Dominant ${primary} · ${row.scores[primary].toFixed(1)}% · barycentric mix of 15 category axes`,
      next.categoryColors,
    );
  }
}

function applyFilter() {
  if (!galaxy) return;
  galaxy.setCategoryFilter(rows, activeCategories);
  ui.setLegendState(activeCategories, theme.categoryColors);
  if (selected >= 0 && galaxy.visible[selected] < 0.5) {
    selected = -1;
    rig.clearFollow();
    ui.closeDetail();
    applySizes();
  }
}

function showAllCategories() {
  activeCategories = new Set(CATEGORIES);
  applyFilter();
}

function toggleCategory(key) {
  const allOn = activeCategories.size === CATEGORIES.length;
  if (allOn) {
    activeCategories = new Set([key]);
    applyFilter();
    frameCategory(key);
    return;
  }
  if (activeCategories.has(key) && activeCategories.size === 1) {
    showAllCategories();
    return;
  }
  if (activeCategories.has(key)) activeCategories.delete(key);
  else activeCategories.add(key);
  if (activeCategories.size === 0) {
    showAllCategories();
    return;
  }
  applyFilter();
}

function frameCategory(key) {
  clusterTmp.set(0, 0, 0);
  let n = 0;
  for (let i = 0; i < rows.length; i += 1) {
    if (galaxy.visible[i] < 0.5) continue;
    if (primaryCategory(rows[i].scores) !== key) continue;
    clusterTmp.x += galaxy.positions[i * 3];
    clusterTmp.y += galaxy.positions[i * 3 + 1];
    clusterTmp.z += galaxy.positions[i * 3 + 2];
    n += 1;
  }
  if (!n) return;
  clusterTmp.multiplyScalar(1 / n);
  rig.flyTo(clusterTmp, 36);
}

function selectWord(index) {
  if (!galaxy || index < 0) return;
  if (galaxy.visible[index] < 0.5) return;
  selected = index;
  const row = rows[index];
  galaxy.driftedPosition(index, clock.elapsedTime, tmp);
  rig.follow(index, tmp);
  const primary = primaryCategory(row.scores);
  const extra = `Dominant ${primary} · ${row.scores[primary].toFixed(1)}% · barycentric mix of 15 category axes`;
  ui.openDetail(row, extra, theme.categoryColors);
  applySizes();
}

function clearSelection() {
  selected = -1;
  rig.clearFollow();
  ui.closeDetail();
  applySizes();
}

function overview() {
  clearSelection();
  rig.resetOverview();
}

function applySizes() {
  const attr = galaxy.points.geometry.getAttribute("aSize");
  for (let i = 0; i < baseSizes.length; i += 1) {
    let size = baseSizes[i];
    if (i === selected) size *= 2.35;
    else if (i === hovered) size *= 1.55;
    attr.array[i] = size;
  }
  attr.needsUpdate = true;
}

function pointerNdcFromEvent(event) {
  const rect = canvas.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  return { x, y };
}

canvas.addEventListener("pointerenter", () => {
  pointerInside = true;
});
canvas.addEventListener("pointerleave", () => {
  pointerInside = false;
  hovered = -1;
  ui.setHover("");
  if (galaxy) applySizes();
});
canvas.addEventListener("pointermove", (event) => {
  if (event.target !== canvas) return;
  pointerNdc = pointerNdcFromEvent(event);
});
canvas.addEventListener("pointerdown", (event) => {
  if (event.button !== 0) return;
  downX = event.clientX;
  downY = event.clientY;
});
canvas.addEventListener("pointerup", (event) => {
  if (event.button !== 0 || !galaxy) return;
  const moved = Math.hypot(event.clientX - downX, event.clientY - downY);
  if (moved > 5) return;
  const ndc = pointerNdcFromEvent(event);
  const hit = pickNearest(
    galaxy.positions,
    camera,
    ndc.x,
    ndc.y,
    canvas.clientWidth,
    canvas.clientHeight,
    clock.elapsedTime,
    galaxy.phases,
    16,
    galaxy.visible,
  );
  if (hit >= 0) selectWord(hit);
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    if (rig.isFollowing()) overview();
    else clearSelection();
  }
  if (event.key === "0" || event.key === "Home") overview();
  if (event.key === "+" || event.key === "=") rig.dolly(0.72);
  if (event.key === "-" || event.key === "_") {
    if (rig.isFollowing()) overview();
    else rig.dolly(1.38);
  }
  if (event.key === "/" && document.activeElement.tagName !== "INPUT") {
    event.preventDefault();
    document.querySelector("#search-input").focus();
  }
});

function viewportSize() {
  const view = window.visualViewport;
  const w = Math.max(1, Math.round(view ? view.width : window.innerWidth));
  const h = Math.max(1, Math.round(view ? view.height : window.innerHeight));
  return { w, h };
}

function fitViewport() {
  const { w, h } = viewportSize();
  const root = document.documentElement;
  root.classList.toggle("is-phone", w < 720);
  root.classList.toggle("is-compact", w < 420 || (w < 900 && h < 420));
  root.classList.toggle("is-tablet", w >= 720 && w < 1100);
  root.classList.toggle("is-short", h < 500);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
  renderer.setSize(w, h, false);
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  if (galaxy) galaxy.setPixelRatio(window.devicePixelRatio || 1);
  rig.resize();
}

window.addEventListener("resize", fitViewport);
window.addEventListener("orientationchange", () => {
  window.setTimeout(fitViewport, 180);
});
if (window.visualViewport) {
  window.visualViewport.addEventListener("resize", fitViewport);
  window.visualViewport.addEventListener("scroll", fitViewport);
}
fitViewport();

function frame() {
  const dt = Math.min(0.033, clock.getDelta());
  const time = clock.elapsedTime;
  if (!galaxy) return;
  galaxy.setTime(time);

  if (selected >= 0) {
    galaxy.driftedPosition(selected, time, tmp);
    galaxy.marker.visible = true;
    galaxy.marker.position.copy(tmp);
    galaxy.marker.lookAt(camera.position);
    const pulse = 1.08 + Math.sin(time * 3.2) * 0.14;
    galaxy.marker.scale.setScalar(pulse);
  } else {
    galaxy.marker.visible = false;
  }

  if (rig.isFollowing() && selected >= 0) {
    rig.update(dt, tmp);
  } else {
    rig.update(dt, null);
  }

  hoverTimer += dt;
  if (pointerInside && !rig.isFollowing() && hoverTimer > 0.05) {
    hoverTimer = 0;
    const hit = pickNearest(
      galaxy.positions,
      camera,
      pointerNdc.x,
      pointerNdc.y,
      canvas.clientWidth,
      canvas.clientHeight,
      time,
      galaxy.phases,
      12,
      galaxy.visible,
    );
    if (hit !== hovered) {
      hovered = hit;
      ui.setHover(hit >= 0 ? rows[hit].word.replaceAll("_", " ") : "");
      applySizes();
    }
  }

  labelTimer += dt;
  const dist = camera.position.distanceTo(rig.controls.target);
  const zoomedIn = dist < 58 || rig.isFollowing();
  if (labelTimer > 0.08) {
    labelTimer = 0;
    if (zoomedIn) {
      clusterLabels.clear();
      const nearby = collectNearbyLabels(
        galaxy.positions,
        galaxy.phases,
        galaxy.sizes,
        camera,
        time,
        viewportSize().w < 720 ? 36 : 80,
        galaxy.visible,
      );
      for (const item of nearby) item.word = rows[item.index].word.replaceAll("_", " ");
      if (selected >= 0 && !nearby.some((item) => item.index === selected)) {
        galaxy.driftedPosition(selected, time, tmp);
        const p = tmp.clone().project(camera);
        nearby.unshift({
          index: selected,
          word: rows[selected].word.replaceAll("_", " "),
          ndcX: p.x,
          ndcY: p.y,
          dist: camera.position.distanceTo(tmp),
        });
      }
      labels.render(nearby, canvas.clientWidth, canvas.clientHeight, selected, selectWord);
    } else {
      labels.clear();
      clusterLabels.render(
        galaxy.centers,
        camera,
        canvas.clientWidth,
        canvas.clientHeight,
        activeCategories,
        theme.categoryColors,
        toggleCategory,
      );
    }
  }

  renderer.render(scene, camera);
  fpsFrames += 1;
  fpsAccum += dt;
  if (fpsAccum >= 0.5) {
    ui.setFps(Math.round(fpsFrames / fpsAccum));
    fpsFrames = 0;
    fpsAccum = 0;
  }
}

boot().catch((error) => {
  loaderCopy.textContent = error.message;
  loaderBar.style.width = "100%";
  loaderBar.classList.add("is-error");
  console.error(error);
});
